/**
 * PhasePermissionsTable - Tabela compartilhada de matriz de permissões por fase
 *
 * Componente genérico reutilizável entre Tarefas e CRM (Negócios) para exibir
 * e editar a matriz [fase × ação] de permissões granulares.
 *
 * Suporta agrupamento por categoria (Tarefas) ou por pipeline (CRM) através da prop
 * `groups`. As ações exibidas são definidas dinamicamente em `columns`, permitindo
 * que cada módulo controle exatamente quais checkboxes aparecem.
 *
 * Features:
 *  - Toggle individual por célula (fase × ação)
 *  - Toggle de coluna inteira (clique no header)
 *  - Toggle de linha inteira (clique na célula da fase)
 *  - Destaque visual por grupo (categoria/pipeline)
 *
 * @version 1.0.0
 * RELEASE: 2026-04-23 v1.0.0 - Criação inicial (extraído de TaskPhasePermissions)
 */

import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Info } from 'lucide-react';
import { Fragment } from 'react';

/** Definição de uma coluna (ação) na matriz */
export interface PhasePermissionColumn {
  /** Chave da ação (ex.: 'can_view', 'can_edit', etc.) */
  key: string;
  /** Label curto exibido no header */
  label: string;
  /** Tooltip descritivo */
  description: string;
}

/** Definição de uma fase exibida na tabela */
export interface PhaseRow {
  id: string;
  name: string;
  color: string;
  /** Identificador do grupo a que a fase pertence (categoria_id ou pipeline_id) */
  group_id: string;
}

/** Definição de um grupo (categoria ou pipeline) */
export interface PhaseGroup {
  id: string;
  name: string;
  color: string;
}

/** Estado de permissão para uma fase específica */
export type PhasePermissionState = {
  phase_id: string;
  [action: string]: boolean | string;
};

interface PhasePermissionsTableProps {
  /** Fases ordenadas para exibição */
  phases: PhaseRow[];
  /** Grupos (categoria/pipeline) para separadores visuais */
  groups: PhaseGroup[];
  /** Mapa phase_id → estado de permissões */
  permissions: Map<string, PhasePermissionState>;
  /** Colunas (ações) a exibir */
  columns: readonly PhasePermissionColumn[];
  /** Callback ao alternar uma célula individual */
  onTogglePermission: (phaseId: string, columnKey: string) => void;
  /** Callback ao alternar uma coluna inteira */
  onToggleColumn: (columnKey: string) => void;
  /** Callback ao alternar uma linha (fase) inteira */
  onToggleRow: (phaseId: string) => void;
  /** Texto descritivo opcional exibido no header do card */
  description?: string;
  /** Badge opcional exibido no topo (ex.: "Qualquer Usuário", "Usuário Responsável") */
  headerBadge?: React.ReactNode;
  /** Label do agrupamento exibido nos separadores (ex.: "categoria" ou "pipeline") */
  groupLabel?: string;
}

/**
 * Tabela genérica de matriz de permissões por fase.
 * Agnóstica a módulo (serve tanto Tarefas quanto CRM).
 */
export function PhasePermissionsTable({
  phases,
  groups,
  permissions,
  columns,
  onTogglePermission,
  onToggleColumn,
  onToggleRow,
  description,
  headerBadge,
  groupLabel = 'grupo',
}: PhasePermissionsTableProps) {
  // Agrupar fases pelo group_id mantendo a ordem dos `groups`
  const groupedPhases = groups.map((g) => ({
    group: g,
    phases: phases.filter((p) => p.group_id === g.id),
  }));

  return (
    <Card>
      <CardHeader className="pb-3">
        {(headerBadge || description) && (
          <>
            {headerBadge && (
              <div className="flex items-center gap-2">
                {headerBadge}
                <Badge variant="outline" className="text-xs">
                  {phases.length} fases
                </Badge>
              </div>
            )}
            {description && (
              <CardDescription className="flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5 shrink-0" />
                {description}
              </CardDescription>
            )}
          </>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="min-w-[200px] font-semibold">Fase</TableHead>
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    className="text-center min-w-[70px] cursor-pointer hover:bg-muted/50 transition-colors"
                    title={`${col.description} — Clique para marcar/desmarcar todas`}
                    onClick={() => onToggleColumn(col.key)}
                  >
                    <span className="text-xs font-medium">{col.label}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedPhases.map(({ group, phases: groupPhases }) => (
                <Fragment key={`grp-${group.id}`}>
                  {/* Separador do grupo (categoria/pipeline) */}
                  <TableRow className="bg-muted/20 hover:bg-muted/20">
                    <TableCell colSpan={columns.length + 1} className="py-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: group.color }}
                        />
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {group.name}
                        </span>
                        <span className="text-[10px] text-muted-foreground/70">
                          ({groupPhases.length} {groupLabel}{groupPhases.length === 1 ? '' : 's'})
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Fases do grupo */}
                  {groupPhases.map((phase) => {
                    const perm = permissions.get(phase.id);
                    return (
                      <TableRow key={phase.id} className="hover:bg-muted/30">
                        <TableCell
                          className="cursor-pointer hover:text-primary transition-colors"
                          onClick={() => onToggleRow(phase.id)}
                          title="Clique para marcar/desmarcar todas as permissões desta fase"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="h-3 w-3 rounded-sm shrink-0"
                              style={{ backgroundColor: phase.color }}
                            />
                            <span className="text-sm font-medium">{phase.name}</span>
                          </div>
                        </TableCell>
                        {columns.map((col) => (
                          <TableCell key={col.key} className="text-center">
                            <Checkbox
                              checked={perm?.[col.key] === true}
                              onCheckedChange={() => onTogglePermission(phase.id, col.key)}
                              className="mx-auto"
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
